import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Tile } from "@carbon/react";
import { TimePoint } from "../../../types";
import { toMonthlySeries } from "./chartData";
import "./analyticsCharts.scss";

interface OffersBarChartProps {
  timeSeries: TimePoint[];
  /** Active currency: only this currency's points are plotted. */
  currency: string;
}

// Palette is `--cds-link-primary`/`--cds-support-*` with literal fallbacks — no `--cds-chart-*`
// tokens ship with this Carbon version, and an undefined `var()` voids the whole declaration.
const AWARDED_COLOR = "var(--cds-support-success, #24a148)";
const RECEIVED_COLOR = "var(--cds-link-primary, #0f62fe)";
const AXIS_COLOR = "var(--cds-text-secondary, #525252)";

export const OffersTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number }>;
  label?: string;
}) => {
  const { t } = useTranslation();
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const nameOf = (key: string) =>
    key === "awards"
      ? t("analytics.charts.awarded")
      : t("analytics.charts.received");
  return (
    <div className="analytics-chart__tooltip">
      <p className="analytics-chart__tooltip-title">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="analytics-chart__tooltip-row">
          {`${nameOf(p.dataKey)}: ${p.value}`}
        </p>
      ))}
    </div>
  );
};

const OffersBarChart = ({ timeSeries, currency }: OffersBarChartProps) => {
  const { t, i18n } = useTranslation();
  const data = toMonthlySeries(timeSeries, currency, i18n.language);

  if (data.length === 0) {
    return (
      <Tile className="analytics-chart">
        <h3 className="analytics-chart__title">
          {t("analytics.charts.offersOverTime")}
        </h3>
        <p className="analytics-chart__empty">{t("analytics.charts.noData")}</p>
      </Tile>
    );
  }

  return (
    <Tile className="analytics-chart">
      <h3 className="analytics-chart__title">
        {t("analytics.charts.offersOverTime")}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--cds-border-subtle, #e0e0e0)"
          />
          <XAxis
            dataKey="label"
            fontSize={12}
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
          />
          <YAxis
            allowDecimals={false}
            stroke={AXIS_COLOR}
            tick={{ fill: AXIS_COLOR }}
          />
          <Tooltip
            content={<OffersTooltip />}
            cursor={{ fill: "var(--cds-layer-hover, #e8e8e8)" }}
          />
          <Legend
            formatter={(value) =>
              value === "awards"
                ? t("analytics.charts.awarded")
                : t("analytics.charts.received")
            }
          />
          <Bar dataKey="awards" fill={AWARDED_COLOR} />
          <Bar dataKey="offersReceived" fill={RECEIVED_COLOR} />
        </BarChart>
      </ResponsiveContainer>
    </Tile>
  );
};

export default OffersBarChart;
